/** Per-tenant build config. Vodafone defaults preserved when env vars are unset —
 *  the release/vodafone build needs NO env change (ADR-0006 freeze). Invalid values fail loud. */
function intEnv(name: string, fallback: number, min: number, max: number): number {
  const raw = import.meta.env[name] as string | undefined
  if (raw === undefined || raw === '') return fallback
  const n = Number(raw)
  if (!Number.isInteger(n) || n < min || n > max)
    throw new Error(`${name} must be an integer in [${min},${max}], got "${raw}"`)
  return n
}
// Empty string counts as unset ONLY for detection; a set-but-invalid value throws.
// GOTCHA this guards: dotenv treats an unquoted `#` as a comment start, so
// VITE_BRAND_COLOR=#2E7D32 silently parses as "" — quote hex values in .env files.
function hexColorEnv(name: string, fallback: string): string {
  const raw = import.meta.env[name] as string | undefined
  if (raw === undefined) return fallback
  if (!/^#[0-9a-fA-F]{6}$/.test(raw))
    throw new Error(
      `${name} must be a #rrggbb hex colour, got "${raw}" (quote the value — unquoted # starts a dotenv comment)`,
    )
  return raw
}
function stringEnv(name: string, fallback: string): string {
  const raw = import.meta.env[name] as string | undefined
  if (raw === undefined) return fallback
  if (raw.trim() === '') throw new Error(`${name} is set but empty/blank`)
  return raw
}
// Selects the advisor knowledge overlay (grounding/knowledge/index.ts). Absent → null =
// core corpus only (Vodafone/prod-t1 builds set nothing). Set-but-blank throws (same loud
// contract as the others); a set value is trimmed and must be a lowercase token matching the
// shape below — an uppercase or malformed key throws rather than being silently normalised.
function tenantKeyEnv(name: string): string | null {
  const raw = import.meta.env[name] as string | undefined
  if (raw === undefined) return null
  const trimmed = raw.trim()
  if (trimmed === '') throw new Error(`${name} is set but empty/blank`)
  if (!/^[a-z][a-z0-9-]*$/.test(trimmed))
    throw new Error(`${name} must be a lowercase token matching /^[a-z][a-z0-9-]*$/, got "${raw}"`)
  return trimmed
}
// Optional tenant logo (spec §5). Absent → null = the built-in econworks asset.
// Set-but-blank throws (same loud contract as the siblings). Value is a URL or
// site-absolute path baked at build time; no format validation beyond non-blank.
function optionalStringEnv(name: string): string | null {
  const raw = import.meta.env[name] as string | undefined
  if (raw === undefined) return null
  if (raw.trim() === '') throw new Error(`${name} is set but empty/blank`)
  return raw.trim()
}
export const FISCAL_YEAR_START_MONTH = intEnv('VITE_FISCAL_YEAR_START_MONTH', 3, 0, 11) // 0-indexed; 3=April (Vodafone)
export const BRAND_COLOR = hexColorEnv('VITE_BRAND_COLOR', '#E60000')
export const PRODUCT_TITLE = stringEnv('VITE_PRODUCT_TITLE', 'Aleph for MMMv3')
export const TENANT_KEY = tenantKeyEnv('VITE_TENANT_KEY')
export const LOGO_URL = optionalStringEnv('VITE_LOGO_URL')
